import { z } from 'zod';

import {
  Category_Restrictions,
  Communication_Preference_Opt_In_Status,
  Fuel_And_Convenience_Profile_Operator,
  Retailer_Network_Operator,
  Retailer_Network_User_Type,
} from '@occc/graphql';

import { IS_REQUIRED, isSelectedMoreThen } from '@occc-app/lib/constants/validationMessages';
import { Paths } from 'type-fest';
import { EmployeeTargetingValues } from './types';
import { addCustomIssue as addIssue } from '../../utils';

import { MIN_SELECTED_RETAILER_NETWORKS } from '../../constants';

export const employeeTargetingSchema = z
  .object({
    retailerNetworkUserType: z
      .union([z.nativeEnum(Retailer_Network_User_Type), z.literal('')])
      .optional(),

    operator: z
      .union([z.nativeEnum(Retailer_Network_Operator), z.literal('')])
      .nullable()
      .optional(),
    retailerNetworks: z
      .array(z.object({ uuid: z.string(), name: z.string() }).passthrough())
      .optional(),
  })
  .superRefine((data, ctx) => {
    const { retailerNetworkUserType, operator, retailerNetworks } = data;
    const hasUserType = !!retailerNetworkUserType;
    const hasOperator = !!operator;
    const hasNetworks = Array.isArray(retailerNetworks) && retailerNetworks.length > 0;

    if (!hasOperator && hasNetworks) {
      addIssue(ctx, 'operator' satisfies Paths<EmployeeTargetingValues>);
    }

    if ((hasOperator || hasNetworks) && !hasUserType) {
      addIssue(ctx, 'retailerNetworkUserType' satisfies Paths<EmployeeTargetingValues>);
    }
    if (hasOperator) {
      if (!hasNetworks) {
        addIssue(ctx, 'retailerNetworks' satisfies Paths<EmployeeTargetingValues>);
      } else if (retailerNetworks.length < MIN_SELECTED_RETAILER_NETWORKS) {
        addIssue(
          ctx,
          'retailerNetworks' satisfies Paths<EmployeeTargetingValues>,
          isSelectedMoreThen(MIN_SELECTED_RETAILER_NETWORKS),
        );
      }
    }
  })
  .optional();

export const fuelAndConvenienceProfileSchema = z
  .object({
    acquisitionChannelAttribute: z.array(z.string()).optional(),
    eaivVerified: z
      .union([
        z.literal(Fuel_And_Convenience_Profile_Operator.IsIn),
        z.literal(Fuel_And_Convenience_Profile_Operator.IsNotIn),
      ])
      .optional(),
    hasACH: z.union([z.literal('true'), z.literal('false'), z.literal('')]).optional(),
    eaivAttributes: z.array(z.nativeEnum(Category_Restrictions)).optional(),
    activeSubBrand: z.union([z.string().uuid(), z.literal('')]).optional(),
  })
  .optional();

const communicationOptInCriterionSchema = z
  .object({
    status: z
      .union([z.nativeEnum(Communication_Preference_Opt_In_Status), z.literal('')])
      .optional(),
    subBrandUuid: z.union([z.string().uuid(), z.literal('')]).optional(),
  })
  .optional();

export const makeCommunicationPreferencesSchema = (areSubBrandsEnabled: boolean) =>
  z
    .object({
      sms: communicationOptInCriterionSchema,
      push: communicationOptInCriterionSchema,
    })
    .superRefine((data, ctx) => {
      (['sms', 'push'] as const).forEach((mechanism) => {
        const criterion = data?.[mechanism];
        // A sub-brand without a status would be silently dropped on save, so require a status.
        if (criterion?.subBrandUuid && !criterion.status) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: IS_REQUIRED,
            path: [mechanism, 'status'],
          });
        }
        if (areSubBrandsEnabled && criterion?.status && !criterion.subBrandUuid) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: IS_REQUIRED,
            path: [mechanism, 'subBrandUuid'],
          });
        }
      });
    })
    .optional();

export const communicationPreferencesSchema = makeCommunicationPreferencesSchema(false);
